import { parse } from "papaparse";
import config from "./config";
import { colors } from "../src/reusable-components";
import { paths } from "./routes";

export const POSITIVE_HEALTH_KEYWORDS = [
  "up", "true", "success", "ok", "healthy", "active",
  "running", "deployed", "connected", "available", "online", "succeeded",
];

export const isPositiveStatus = (status?: string): boolean => {
  if (!status) return false;
  const lower = status.toLowerCase();
  return POSITIVE_HEALTH_KEYWORDS.some((kw) => lower.includes(kw));
};

const _MS_PER_DAY = 1000 * 60 * 60 * 24;

type LandingRule = {
  path: string;
  permission: string;
  requires?: string[];
};

const LANDING_PRIORITY: LandingRule[] = [
  {
    path: paths.identitiesAttributes,
    permission: "campaignplanning",
  },
  {
    path: paths.pastQueries,
    permission: "query",
  },
  {
    path: paths.provRecord,
    permission: "provenancetrail",
  },
  {
    path: paths.activationHistory,
    permission: "activationhistory",
  },
  {
    path: paths.activationChannels,
    permission: "activationconnectors",
  },
  {
    path: paths.datasetManager,
    permission: "datamanager",
  },
];

export const getFirstAccessibleRoute = () => {
  const storedPermissions = JSON.parse(
    localStorage.getItem("userPermissions") || "{}",
  );
  console.log({ storedPermissions });
  for (const route of LANDING_PRIORITY) {
    const level = storedPermissions?.[route.permission];

    if (!level || level === "no") continue;
    if (route.requires && !route.requires.includes(level)) continue;

    return route.path;
  }

  return paths.home;
};

export const getCSSVariable = (variableName: string): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
};

export const getContrastColor = (hex: string): string => {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  // WCAG relative luminance
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 160 ? "#1a1a1a" : "#ffffff";
};

// export const lightenColor = (hex, lightness = 0.95) => {
export const hexToRgbChannels = (hex: string): string => {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  return `${r}, ${g}, ${b}`;
};

export const tintColor = (hex, lightness = 0.95, saturationFactor = 0.71) => {
  let r = parseInt(hex.substring(1, 3), 16);
  let g = parseInt(hex.substring(3, 5), 16);
  let b = parseInt(hex.substring(5, 7), 16);

  let hsl = rgbToHsl(r, g, b);
  
  hsl[1] *= saturationFactor; // Reduce saturation
  hsl[2] = lightness;

  return hslToHex(hsl[0], hsl[1], hsl[2]);
};

export const lightenColorSoft = (hex) => tintColor(hex, 0.88);
export const lightenColor = (hex) => tintColor(hex, 0.95);

const rgbToHsl = (r, g, b) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  ((r /= 255), (g /= 255), (b /= 255));
  let max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h, s, l];
};

const hslToHex = (h, s, l) => {
  let hueToRgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  let p = 2 * l - q;
  let r = hueToRgb(p, q, h + 1 / 3);
  let g = hueToRgb(p, q, h);
  let b = hueToRgb(p, q, h - 1 / 3);

  return (
    `#${Math.round(r * 255)
      .toString(16)
      .padStart(2, "0")}` +
    `${Math.round(g * 255)
      .toString(16)
      .padStart(2, "0")}` +
    `${Math.round(b * 255)
      .toString(16)
      .padStart(2, "0")}`
  );
};

export const findUrlById = (id, mainMenuItems) => {
  for (const menuItem of mainMenuItems) {
    if (menuItem.subMenu) {
      for (const subItem of menuItem.subMenu) {
        if (subItem.id === id) {
          return subItem.urls?.[0] || null;
        }
      }
    }
  }
  return null;
};

export const changeColorShade = (color, darker) => {
  if (
    (color === colors.darkblack && darker) ||
    (color === colors.white && !darker)
  )
    return color;
  const blendColor = darker ? colors.darkblack : colors.white;
  const blendAmount = 0.5;
  const [originalR, originalG, originalB] = color
    .match(/\w\w/g)
    .map((c) => parseInt(c, 16));
  const [blendR, blendG, blendB] = blendColor
    .match(/\w\w/g)
    .map((c) => parseInt(c, 16));
  const r = Math.round(originalR + (blendR - originalR) * blendAmount)
    .toString(16)
    .padStart(2, "0");
  const g = Math.round(originalG + (blendG - originalG) * blendAmount)
    .toString(16)
    .padStart(2, "0");
  const b = Math.round(originalB + (blendB - originalB) * blendAmount)
    .toString(16)
    .padStart(2, "0");
  return "#" + r + g + b;
};

// a and b are javascript Date objects
export const getDuration = (a: Date, b: Date) => {
  if (isNaN(a.getDate()) || isNaN(b.getDate())) return NaN;
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((utc2 - utc1) / _MS_PER_DAY) + 1;
};

export const getDaysElapsed = (start: Date, end: Date, currDate: Date) => {
  if (isNaN(start.getDate()) || isNaN(end.getDate())) return NaN;
  const campaignDuration = getDuration(start, end);
  const diff = getDuration(start, currDate);
  return diff < 0 ? 0 : diff > campaignDuration ? campaignDuration : diff;
};

export const formatNumber = (x: any, decimalPlaces: any = 0) => {
  if (!isFinite(parseFloat(x))) {
    return "";
  }

  //remove commas if present
  const y = x.toString().replace(/,/g, "");

  if (y.includes(".") || decimalPlaces > 0) {
    return Number(y).toLocaleString("en-US", {
      minimumFractionDigits: decimalPlaces || 2,
      maximumFractionDigits: decimalPlaces || 2,
    });
  }
  return Number(y).toLocaleString("en-US");
};

export const getExponent = (v: number | string): number | null => {
  if (!isFinite(parseFloat(v as string))) return null;
  const exponentMatch = Number(v)
    .toExponential()
    .match(/[+-]\d+$/);
  return exponentMatch && Number(exponentMatch[0]);
};

export const getFormattedMatchRate = (matchRate) => {
  const matchRatePerc = calculateKeepNullValues(matchRate, "*", 100);
  if (matchRatePerc > 0 && matchRatePerc < 1) {
    const firstNonZeroDecimalPlace = Math.abs(
      getExponent(matchRatePerc) as number,
    );
    return formatNumber(matchRatePerc, firstNonZeroDecimalPlace);
  }
  return formatNumber(matchRatePerc, 1);
};

export const exportToCsv = (filename, rows) => {
  // if (navigator.msSaveBlob) {
  //   // IE 10+
  //   navigator.msSaveBlob(blob, filename);
  // } else {
  //   const link = document.createElement('a');
  //   if (link.download !== undefined) {
  //     // feature detection
  //     // Browsers that support HTML5 download attribute
  //     const url = URL.createObjectURL(blob);
  //     link.setAttribute('href', url);
  //     link.setAttribute('download', filename);
  //     link.style.visibility = 'hidden';
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   }
  // }
};

export const ellipsisAddress = (str: string) => {
  if (str.length <= 10) return str;
  return str.substr(0, 5) + "..." + str.substr(str.length - 5, str.length);
};

export const getQueryString = (obj: { [key: string]: string }) => {
  return Object.keys(obj)
    .filter((k) => !!obj[k])
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(obj[k]))
    .join("&");
};

export const getDateArray = (cur: Date): string[] => {
  if (!cur || isNaN(cur.getDate())) {
    return [];
  }
  const monthShortname = cur.toLocaleString("default", { month: "short" });
  return [
    toTwoDigits(cur.getDate().toString()),
    monthShortname,
    toTwoDigits(cur.getFullYear().toString()),
  ];
};

// Standard app-wide timestamp format: "10 Aug 2026, 14:05" (DD Mon YYYY, HH:mm)
export const formatDateTime = (
  input: Date | string | number | null | undefined,
): string => {
  const d = input instanceof Date ? input : new Date(input as string | number);
  if (!d || isNaN(d.getTime())) return "—";
  const day = toTwoDigits(d.getDate().toString());
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear().toString();
  const hours = toTwoDigits(d.getHours().toString());
  const minutes = toTwoDigits(d.getMinutes().toString());
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
};

// Date-only variant of formatDateTime, for displays with no time component: "10 Aug 2026"
export const formatDateOnly = (
  input: Date | string | number | null | undefined,
): string => {
  const d = input instanceof Date ? input : new Date(input as string | number);
  if (!d || isNaN(d.getTime())) return "—";
  const day = toTwoDigits(d.getDate().toString());
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear().toString();
  return `${day} ${month} ${year}`;
};

// Same format as formatDateTime, split into date/time so callers can render them on separate lines.
export const formatDateTimeParts = (
  input: Date | string | number | null | undefined,
): { date: string; time: string } | null => {
  const d = input instanceof Date ? input : new Date(input as string | number);
  if (!d || isNaN(d.getTime())) return null;
  const day = toTwoDigits(d.getDate().toString());
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear().toString();
  const hours = toTwoDigits(d.getHours().toString());
  const minutes = toTwoDigits(d.getMinutes().toString());
  return { date: `${day} ${month} ${year},`, time: `${hours}:${minutes}` };
};

export const getRelativeTimeShort = (date: Date): string => {
  const elapsed = Date.now() - date.getTime();
  if (isNaN(elapsed) || elapsed < 0) return "—";
  const s = Math.round(elapsed / 1000);
  const m = Math.round(elapsed / 60_000);
  const h = Math.round(elapsed / 3_600_000);
  const d = Math.round(elapsed / 86_400_000);
  const mo = Math.round(elapsed / (86_400_000 * 30));
  const y = Math.round(elapsed / (86_400_000 * 365));
  if (s < 60)  return s === 0 ? "now" : `${s}s ago`;
  if (m < 60)  return `${m}m ago`;
  if (h < 24)  return `${h}h ago`;
  if (d < 30)  return `${d}d ago`;
  if (mo < 12) return `${mo}mo ago`;
  return `${y}y ago`;
};

export const getTimeDifference = (current, previous) => {
  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const msPerMonth = msPerDay * 30;
  const msPerYear = msPerDay * 365;

  const elapsed = current - previous;

  if (elapsed < msPerMinute) {
    return Math.round(elapsed / 1000) + " seconds ago";
  }

  if (elapsed < msPerHour) {
    return Math.round(elapsed / msPerMinute) + " minutes ago";
  }

  if (elapsed < msPerDay) {
    return Math.round(elapsed / msPerHour) + " hours ago";
  }

  if (elapsed < msPerMonth) {
    return Math.round(elapsed / msPerDay) + " days ago";
  }

  if (elapsed < msPerYear) {
    return Math.round(elapsed / msPerMonth) + " months ago";
  }

  return +Math.round(elapsed / msPerYear) + " years ago";
};

export const getHMSArray = (cur: Date): string[] => {
  if (!cur || isNaN(cur.getDate())) {
    return [];
  }
  return [
    `${cur.getUTCHours()}`,
    `${cur.getUTCMinutes()}`,
    `${cur.getUTCSeconds()}`,
  ].map(toTwoDigits);
};

export const toTwoDigits = (s: string): string => {
  const num = parseInt(s);
  return `${num}`.length === 1 ? `0${num}` : `${num}`;
};

export const moveCursorToEndOfInput = (event: any) => {
  const el = event.target;
  if (typeof el.selectionStart === "number") {
    el.selectionStart = el.selectionEnd = el.value.length;
  } else if (typeof el.createTextRange !== "undefined") {
    el.focus();
    const range = el.createTextRange();
    range.collapse(false);
    range.select();
  }
};

export const isEmpty = (obj: Object | undefined) =>
  !obj || Object.keys(obj).length === 0;

export const addMonths = (date: Date, diff: number) => {
  if (isNaN(date.getDate())) return;
  const targetDayOfMonth = date.getDate();
  const newDate = date;
  newDate.setMonth(newDate.getMonth() + diff);
  if (targetDayOfMonth > 28 && newDate.getDate() !== targetDayOfMonth) {
    newDate.setDate(0); // Set date to last day of month
  }
};

// returns null if either operand is null/undefined/NaN/Infinity or if division by 0
export const calculateKeepNullValues = (x: any, operator: string, y: any) => {
  const calculate = (x, y) =>
    operator === "+"
      ? x + y
      : operator === "-"
        ? x - y
        : operator === "*"
          ? x * y
          : operator === "/"
            ? isFinite(x / y)
              ? x / y
              : null
            : null;
  return !isFinite(parseFloat(x)) || !isFinite(parseFloat(y))
    ? null
    : calculate(x, y);
};

// returns null only if both operands are null/undefined/NaN/Infinity, or if division by 0
export const calculateIgnoreNullValues = (x: any, operator: string, y: any) => {
  const calculate = (x, y) =>
    operator === "+"
      ? x + y
      : operator === "-"
        ? x - y
        : operator === "*"
          ? x * y
          : operator === "/"
            ? isFinite(x / y)
              ? x / y
              : null
            : null;
  return isFinite(parseFloat(x)) && isFinite(parseFloat(y))
    ? calculate(x, y)
    : isFinite(parseFloat(x))
      ? x
      : isFinite(parseFloat(y))
        ? operator === "-"
          ? -y
          : y
        : null;
};

export const mergeObjects = (obj1, obj2) => {
  if (!obj1 && !obj2) return null;
  if (!obj1) return obj2;
  if (!obj2) return obj1;

  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
  return Array.from(allKeys).reduce(
    (acc, key) => ({
      ...acc,
      [key]: calculateIgnoreNullValues(obj1[key], "+", obj2[key]),
    }),
    {},
  );
};

export const getFilledArray = (startDate: Date, endDate: Date) => {
  const campaignDuration = getDuration(startDate, endDate);
  const filledArray = new Array(campaignDuration).fill({}).map(
    (
      item,
      index,
    ): {
      x: string;
      y: any;
    } => {
      const date = new Date(startDate);
      date.setUTCHours(0, 0, 0, 0);
      date.setDate(date.getDate() + index);
      return {
        x: date.toISOString(),
        y: null,
      };
    },
  );
  return filledArray;
};

export const capitalize = (a: string) => {
  if (typeof a !== "string") return "";
  return a.charAt(0).toUpperCase() + a.substr(1);
};

export const toTitleCase = (a: string) => {
  if (typeof a !== "string") return "";
  return a
    .replace(/[_-]+/g, " ")
    .replace(
      /\w+/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
    );
};

/** Display helper for Adserver names/types — always uppercase in the UI. */
export const formatAdserverType = (
  value: string | number | null | undefined,
): string => {
  if (value == null || value === "") return "";
  return String(value).toUpperCase();
};

export const roundToNearest = (num: number, precision: number) => {
  if (!isFinite(num) || !isFinite(precision) || precision <= 0) return null;
  const result = Math.round(num / precision) * precision;
  return result === -0 ? 0 : result;
};

export const getCommaSeparatedString = (stringArr: string[]) => {
  if (stringArr.length <= 1) {
    return (stringArr && stringArr[0]) || "";
  }

  const commaSeparatedString = stringArr.join(", ");
  const lastCommaIndex = commaSeparatedString.lastIndexOf(",");
  return `${commaSeparatedString.substr(
    0,
    lastCommaIndex,
  )} and ${commaSeparatedString.substr(lastCommaIndex + 2)}`;
};

export const validateSpotUnits = (fieldValue, subFields) => {
  const missingUnits = new Set<string>();
  const optionsWithMissingUnits = new Set<string>();

  fieldValue &&
    fieldValue.forEach((selectedOption) => {
      subFields &&
        subFields.forEach((subField) => {
          if (!selectedOption[subField.name]) {
            optionsWithMissingUnits.add(selectedOption.label);
            missingUnits.add(subField.label);
          }
        });
    });

  if (isEmpty(Array.from(missingUnits))) {
    return "";
  }

  const errorMsg = `*Please enter ${getCommaSeparatedString(
    Array.from(missingUnits),
  )} for ${getCommaSeparatedString(Array.from(optionsWithMissingUnits))}`;

  return errorMsg;
};

export const validateSubFields = (fieldValue, subFields) => {
  const invalidSubfields = new Set<string>();
  const invalidOptions = new Set<string>();

  fieldValue &&
    fieldValue.forEach((selectedOption) => {
      subFields &&
        subFields.forEach((subField) => {
          if (
            isEmpty(selectedOption[subField.name]) &&
            selectedOption[subField.name] !== "0"
          ) {
            invalidOptions.add(selectedOption.label);
            invalidSubfields.add(subField.errorLabel || subField.label);
          }
        });
    });

  if (isEmpty(Array.from(invalidSubfields))) {
    return "";
  }

  const errorMsg = `*Please enter ${getCommaSeparatedString(
    Array.from(invalidSubfields),
  )} for ${getCommaSeparatedString(Array.from(invalidOptions))}.`;

  return errorMsg;
};

export const debounce = (callback, wait) => {
  let timeout;
  return (...args) => {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => callback.apply(context, args), wait);
  };
};

export const previewFile = (csvFile) => {
  return new Promise((resolve, reject) => {
    parse(csvFile, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      preview: 1,
      step: (results, parser) => {
        console.log("RESULTS: ", results);
        resolve(results);
        parser.abort();
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};

export const validateFile = (
  csvFile,
  firstRowTypes,
  setProgressFn,
  validationChunkSize,
) => {
  const isCellEmpty = (cellValue) => {
    // Note: white spaced strings are not considered empty
    return cellValue !== 0 && !Boolean(cellValue);
  };
  const isIdentifier = (fieldName) =>
    config.csv_identifiers.includes(fieldName);

  return new Promise((resolve, reject) => {
    let rowCounter = 0;
    const parsingErrors: any = [];
    parse(csvFile, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      worker: true,
      chunk: (results, parser) => {
        const { data: rows, meta } = results;
        // console.log('Chunk Results: ', results);
        rows.forEach((row, rowIndex) => {
          const rowInChunk = rowIndex + 1;
          const rowInFile = rowCounter + rowInChunk + 1; // add 1 for header row
          meta.fields.forEach((field, fieldIndex) => {
            if (isIdentifier(field) && isCellEmpty(row[field])) {
              parsingErrors.push(
                `Row ${rowInFile}: Empty cell in identifier column '${field}'`,
              );
            }
            if (typeof row[field] === "number" && !isFinite(row[field])) {
              parsingErrors.push(
                `Row ${rowInFile}: Invalid number in column '${field}'`,
              );
            }
            if (firstRowTypes[fieldIndex] !== typeof row[field]) {
              if (!isCellEmpty) {
                parsingErrors.push(
                  `Row ${rowInFile}: Data type mismatch in '${field}' column`,
                );
              }
            }
          });
          if (parsingErrors.length > 0) {
            reject(parsingErrors);
            // Wrap in try/catch block to prevent app crash due to error in papaparse when worker is enabled:
            // TypeError: Cannot read property 'userComplete' of undefined
            try {
              parser.abort();
            } catch (err) {
              console.error("Abort error", err);
            }
          }
        });
        rowCounter += rows.length;
        setProgressFn((state) => state + 1);
      },
      chunkSize: validationChunkSize,
      complete: () => resolve(rowCounter),
      error: (error) => {
        console.error("Error: ", error);
        reject(error);
      },
    });
  });
};

// Calendar-month boundaries for "last month" (1st through the last day),
// as plain Date objects — callers format to whatever string shape they need.
export const getLastMonthRange = (): { start: Date; end: Date } => {
  const n = new Date();
  return {
    start: new Date(n.getFullYear(), n.getMonth() - 1, 1),
    end: new Date(n.getFullYear(), n.getMonth(), 0),
  };
};

// YYYY-MM-DD, in local time (not UTC, so it matches what a <input type="date"> shows)
export const toDateValue = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// FQS query endpoints (query, query/aggregate, query/intermediate*) return
// 201 with a populated `warnings` array when the query itself started but a
// non-fatal side effect failed (org lookup, conversation-turn save, or
// state-machine-record save). Absent/empty `warnings` means full success.
export const getQueryWarningMessage = (response: {
  warnings?: string[];
}): string | null => {
  if (!response?.warnings?.length) return null;
  return `Query started, but: ${response.warnings.join(" ")}`;
};

export const getInitials = (firstName: string, lastName: string = "") => {
  if (!firstName) return "";
  if (firstName && lastName) {
    return firstName.charAt(0) + lastName.charAt(0);
  }
  const initialsArr = firstName.split(" ");
  const firstInitial = initialsArr[0].charAt(0);
  const secondInitial =
    initialsArr.length > 1 ? initialsArr[initialsArr.length - 1].charAt(0) : "";
  return firstInitial + secondInitial;
};
